/**
 * Estado compartilhado da Área do PCP: o pacote guardado no aparelho
 * (manuais ativos + execuções abertas + motivos), a fila de toques ainda não
 * enviados e a situação da conexão (online, offline, alterações pendentes).
 *
 * Todo toque na execução passa por `registrar`: entra na fila, a tela já
 * mostra o efeito pela projeção local, e o envio acontece em seguida — ou
 * quando a conexão voltar, com recuo de 5 s a 60 s entre tentativas.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { ehFalhaDeRede, enviarEventos, lerPacoteOffline } from "../../../services/areaPcp";
import { codigoTenantCorrente } from "../../../utils/tenantStorage";
import { CHAVE_PACOTE, criarArmazem, nomeDaBase } from "./armazem";
import { criarFila, proximoIntervalo } from "./fila";

const AreaPcpContext = createContext(null);
const CHAVE_DISPOSITIVO = "pharuerp:area-pcp:dispositivo";

export const CONEXAO = Object.freeze({ ONLINE: "online", OFFLINE: "offline", PENDENTE: "pendente" });

/** Id do aparelho, para dizer "outro aparelho mexeu neste lote". */
function idDoAparelho() {
  try {
    const salvo = window.localStorage.getItem(CHAVE_DISPOSITIVO);
    if (salvo) return salvo;
    const novo = `ap-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(CHAVE_DISPOSITIVO, novo);
    return novo;
  } catch {
    return "ap-sem-storage";
  }
}

function navegadorOnline() {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

function substituirExecucao(pacote, detalhe) {
  if (!pacote || !detalhe?.id) return pacote;
  const outras = (pacote.execucoes || []).filter((e) => e.id !== detalhe.id);
  return { ...pacote, execucoes: [...outras, detalhe] };
}

export function AreaPcpProvider({ children }) {
  const { request, usuario } = useAuth();
  const toast = useToast();
  const dispositivo = useMemo(idDoAparelho, []);
  const armazem = useMemo(
    () => criarArmazem(nomeDaBase(codigoTenantCorrente(), usuario?.id ?? "anonimo")),
    [usuario?.id],
  );
  const fila = useMemo(
    () => criarFila({
      armazem,
      enviar: (execucaoId, eventos) => enviarEventos(request, execucaoId, eventos, dispositivo),
      ehFalhaDeRede,
    }),
    [armazem, request, dispositivo],
  );

  const [pacote, setPacote] = useState(null);
  const [erroPacote, setErroPacote] = useState(null);
  const [pendentes, setPendentes] = useState({});
  const [online, setOnline] = useState(navegadorOnline);
  const [semRede, setSemRede] = useState(false);
  const tentativas = useRef(0);
  const timer = useRef(null);

  const guardarPacote = useCallback(
    (proximo) => {
      setPacote(proximo);
      armazem.gravar(CHAVE_PACOTE, proximo).catch(() => {});
    },
    [armazem],
  );

  const carregarPacote = useCallback(async () => {
    try {
      const novo = await lerPacoteOffline(request);
      guardarPacote(novo);
      setErroPacote(null);
      setSemRede(false);
      return novo;
    } catch (erro) {
      if (ehFalhaDeRede(erro)) setSemRede(true);
      else setErroPacote(erro);
      return null;
    }
  }, [request, guardarPacote]);

  const atualizarExecucao = useCallback(
    (detalhe) => {
      setPacote((atual) => {
        const proximo = substituirExecucao(atual, detalhe);
        armazem.gravar(CHAVE_PACOTE, proximo).catch(() => {});
        return proximo;
      });
    },
    [armazem],
  );

  const sincronizar = useCallback(async () => {
    clearTimeout(timer.current);
    const resultado = await fila.sincronizar();
    setPendentes(await fila.pendentesPorExecucao());
    resultado.respostas.forEach(({ resposta }) => {
      atualizarExecucao(resposta);
      if (resposta?.rejeitados?.length) toast.warning(resposta.rejeitados[0].detalhe);
      if (resposta?.conflitos?.length) {
        toast.info("Outro aparelho também mexeu neste lote. Valeu o registro mais recente de cada passo.");
      }
    });
    resultado.recusados.forEach(({ erro }) => toast.error(erro.message));
    if (resultado.semRede) {
      setSemRede(true);
      timer.current = setTimeout(() => {
        sincronizar();
      }, proximoIntervalo(tentativas.current));
      tentativas.current += 1;
    } else {
      setSemRede(false);
      tentativas.current = 0;
    }
    return resultado;
  }, [fila, atualizarExecucao, toast]);

  const registrar = useCallback(
    async (execucaoId, eventos) => {
      if (!eventos.length) return;
      await fila.acrescentar(execucaoId, eventos);
      setPendentes(await fila.pendentesPorExecucao());
      sincronizar();
    },
    [fila, sincronizar],
  );

  useEffect(() => {
    let ativo = true;
    armazem.ler(CHAVE_PACOTE).then((guardado) => {
      if (ativo && guardado) setPacote((atual) => atual || guardado);
    }).catch(() => {});
    fila.pendentesPorExecucao().then((p) => {
      if (!ativo) return;
      setPendentes(p);
      if (Object.keys(p).length) sincronizar();
    }).catch(() => {});
    carregarPacote();
    return () => {
      ativo = false;
      clearTimeout(timer.current);
    };
  }, [armazem, fila, carregarPacote, sincronizar]);

  useEffect(() => {
    const aoVoltar = () => {
      setOnline(true);
      tentativas.current = 0;
      sincronizar().then(() => carregarPacote());
    };
    const aoCair = () => setOnline(false);
    window.addEventListener("online", aoVoltar);
    window.addEventListener("offline", aoCair);
    return () => {
      window.removeEventListener("online", aoVoltar);
      window.removeEventListener("offline", aoCair);
    };
  }, [sincronizar, carregarPacote]);

  const quantidadePendente = Object.values(pendentes).reduce((soma, lista) => soma + lista.length, 0);
  let conexao = CONEXAO.ONLINE;
  if (!online || semRede) conexao = CONEXAO.OFFLINE;
  else if (quantidadePendente) conexao = CONEXAO.PENDENTE;

  const valor = useMemo(
    () => ({
      pacote,
      erroPacote,
      carregarPacote,
      atualizarExecucao,
      registrar,
      sincronizar,
      pendentes,
      quantidadePendente,
      conexao,
      dispositivo,
    }),
    [pacote, erroPacote, carregarPacote, atualizarExecucao, registrar, sincronizar, pendentes, quantidadePendente, conexao, dispositivo],
  );

  return <AreaPcpContext.Provider value={valor}>{children}</AreaPcpContext.Provider>;
}

export function useAreaPcp() {
  const contexto = useContext(AreaPcpContext);
  if (!contexto) throw new Error("useAreaPcp fora do AreaPcpProvider");
  return contexto;
}
